import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BridgeClubLayout from '../../components/BridgeClub/BridgeClubLayout';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import { useLocalAccess } from '../../hooks/useLocalAccess';
import './ScanAndGoReceipt.css';

interface ReceiptItem {
  id: number;
  product: {
    id: number;
    name: string;
    price: string;
  };
  quantity: number;
  weight_lbs?: string;
  is_weighted: boolean;
  line_total: string;
}

interface ReceiptData {
  session_id: string;
  status: string;
  items: ReceiptItem[];
  subtotal: string;
  tax: string;
  total: string;
  item_count: number;
  paid_at: string;
  card_type?: string;
  last_four?: string;
  transaction_id?: string;
}

const ScanAndGoReceipt: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { hasLocalAccess } = useLocalAccess();
  const [receipt, setReceipt] = useState<ReceiptData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!hasLocalAccess) {
      navigate('/bridge-club');
      return;
    }
    if (sessionId) {
      loadReceipt();
    }
  }, [hasLocalAccess, navigate, sessionId]);

  const loadReceipt = async () => {
    try {
      setLoading(true);
      const data = await bridgeClubAPI.getScanAndGoOrder(sessionId!);
      setReceipt(data);
    } catch (err) {
      console.error('Failed to load receipt:', err);
      setError('Failed to load receipt');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <BridgeClubLayout>
        <div className="receipt-loading">
          <div className="spinner"></div>
          <p>Loading receipt...</p>
        </div>
      </BridgeClubLayout>
    );
  }

  if (error || !receipt) {
    return (
      <BridgeClubLayout>
        <div className="receipt-error">
          <p>{error || 'Receipt not found'}</p>
          <button onClick={() => navigate('/bridge-club')}>
            Return to Dashboard
          </button>
        </div>
      </BridgeClubLayout>
    );
  }

  return (
    <BridgeClubLayout>
      <div className="scan-receipt">
        {/* Success Header */}
        <div className="receipt-header">
          <div className="success-icon">✓</div>
          <h2>Payment Successful</h2>
          <p className="receipt-date">{formatDate(receipt.paid_at)}</p>
        </div>

        {/* Show to Staff Notice */}
        <div className="staff-notice">
          <div className="notice-icon">📱</div>
          <div className="notice-text">
            <strong>Show this to staff at the exit</strong>
            <p>Staff will verify your purchase before you leave</p>
          </div>
        </div>

        {/* QR Code / Order ID for staff verification */}
        <div className="verification-code">
          <div className="order-id">{receipt.session_id}</div>
          <p className="order-id-label">Order ID</p>
        </div>

        {/* Items */}
        <div className="receipt-items">
          <h3>Items Purchased ({receipt.item_count})</h3>
          {receipt.items.map((item) => (
            <div key={item.id} className="receipt-item">
              <div className="item-info">
                <span className="item-name">{item.product.name}</span>
                <span className="item-qty">
                  {item.is_weighted ? `${item.weight_lbs} lbs` : `Qty: ${item.quantity}`}
                </span>
              </div>
              <span className="item-price">${parseFloat(item.line_total).toFixed(2)}</span>
            </div>
          ))}
        </div>

        {/* Totals */}
        <div className="receipt-totals">
          <div className="total-row">
            <span>Subtotal</span>
            <span>${parseFloat(receipt.subtotal).toFixed(2)}</span>
          </div>
          <div className="total-row">
            <span>Tax (8.0%)</span>
            <span>${parseFloat(receipt.tax).toFixed(2)}</span>
          </div>
          <div className="total-row grand-total">
            <span>Total</span>
            <span>${parseFloat(receipt.total).toFixed(2)}</span>
          </div>
        </div>

        {/* Payment Info */}
        <div className="payment-info">
          <div className="payment-row">
            <span>Payment Method</span>
            <span>
              {receipt.card_type} ****{receipt.last_four}
            </span>
          </div>
          {receipt.transaction_id && (
            <div className="payment-row">
              <span>Transaction ID</span>
              <span className="transaction-id">{receipt.transaction_id}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="receipt-actions">
          <button
            className="new-order-btn"
            onClick={() => navigate('/bridge-club/scan-and-go')}
          >
            Start New Order
          </button>
          <button
            className="home-btn"
            onClick={() => navigate('/bridge-club')}
          >
            Return to Dashboard
          </button>
        </div>

        {/* Store Info */}
        <div className="store-info">
          <p>Red Bridge Feed & Seed</p>
          <p>Kettle Falls, WA</p>
          <p>Thank you for shopping with us!</p>
        </div>
      </div>
    </BridgeClubLayout>
  );
};

export default ScanAndGoReceipt;
